/** Whether a snapshot field came from BigQuery or illustrative mock data. */
export type ReportFieldDataSource = 'warehouse' | 'mock';

/** Opacity applied in the Care Portal when a field uses mock fallback data. */
export const REPORT_MOCK_FIELD_OPACITY = 0.42;

/** Per-field provenance on merged warehouse + mock snapshots. */
export type ReportSectionFieldSources = Record<string, ReportFieldDataSource>;

/**
 * Reads a field's data source from snapshot metadata (defaults to mock).
 * @param snapshot - Section snapshot JSON.
 * @param field - Field key (e.g. `topNeeds`).
 * @returns Warehouse or mock provenance.
 */
export const readReportFieldSource = (
  snapshot: Record<string, unknown>,
  field: string,
): ReportFieldDataSource => {
  const sources = snapshot.fieldSources;
  if (!sources || typeof sources !== 'object') {
    return 'mock';
  }
  const source = (sources as Record<string, unknown>)[field];
  return source === 'warehouse' ? 'warehouse' : 'mock';
};

/**
 * Picks warehouse data when present; otherwise falls back to mock illustrative data.
 * @param warehouseValue - Value from the warehouse snapshot.
 * @param mockValue - Deterministic mock fallback.
 * @param hasWarehouseData - Whether warehouse data is non-empty for this field.
 * @returns Value and provenance for snapshot metadata.
 */
export const pickReportFieldValue = <T>(
  warehouseValue: T,
  mockValue: T,
  hasWarehouseData: boolean,
): { value: T; source: ReportFieldDataSource } => ({
  value: hasWarehouseData ? warehouseValue : mockValue,
  source: hasWarehouseData ? 'warehouse' : 'mock',
});
